import { useQueryClient } from "@tanstack/react-query";
import { Expand, X } from "lucide-react";
import { lazy, type ReactNode, Suspense, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentCreateOutcomeFrame } from "@/components/shared/finance/DocumentCreateOutcomeFrame";
import { SectionHeaderAction } from "@/components/shared/layout/SectionHeaderAction";
import { ConfirmDialog } from "@/components/shared/utility/ConfirmDialog";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { paymentKeys } from "@/features/vendor-payments/hooks/query-keys";
import type { CreatePurchaseOrderResult } from "@/features/purchase-orders/types";
import type { DocumentCreateKind } from "@/lib/document-create-launcher";

const InvoiceCreateWorkflow = lazy(async () => ({
  default: (await import("@/features/invoices/components/InvoiceCreateWorkflow"))
    .InvoiceCreateWorkflow,
}));
const EstimateCreateWorkflow = lazy(async () => ({
  default: (await import("@/features/estimates/components/EstimateCreateWorkflow"))
    .EstimateCreateWorkflow,
}));
// Loaded together, as on the unified page, so the success screen does not
// replace a just-created dialog with a spinner while its chunk fetches.
const loadPurchaseOrderCreate = () =>
  Promise.all([
    import("@/features/purchase-orders/components/POSuccessScreen"),
    import("@/features/purchase-orders/components/PurchaseOrderForm"),
  ]);
const PurchaseOrderForm = lazy(async () => ({
  default: (await loadPurchaseOrderCreate())[1].PurchaseOrderForm,
}));
const POSuccessScreen = lazy(async () => ({
  default: (await loadPurchaseOrderCreate())[0].POSuccessScreen,
}));

interface DocumentCreateCopy {
  description: string;
  /** The route hosting the same workflow as a full page. */
  route: string;
  title: string;
}

export const DOCUMENT_CREATE_COPY: Record<DocumentCreateKind, DocumentCreateCopy> = {
  estimate: {
    description: "Quote a customer one step at a time without leaving this page.",
    route: "/estimates/create",
    title: "Create Estimate",
  },
  invoice: {
    description: "Bill a customer one step at a time without leaving this page.",
    route: "/invoices/create",
    title: "Create Invoice",
  },
  purchase_order: {
    description: "Order from a vendor one step at a time without leaving this page.",
    route: "/purchase-orders/create",
    title: "Create Purchase Order",
  },
};

type PendingLeave = "close" | "expand";

const LEAVE_COPY: Record<
  PendingLeave,
  { confirmLabel: string; description: string; title: string }
> = {
  close: {
    confirmLabel: "Close",
    description: "Anything entered here that has not been created yet will be discarded.",
    title: "Close this window?",
  },
  expand: {
    confirmLabel: "Open full page",
    description:
      "The full page starts a fresh draft; anything entered here that has not been created yet will be discarded.",
    title: "Continue on the full page?",
  },
};

export interface DocumentCreateDialogProps {
  kind: DocumentCreateKind;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

function DialogPendingState() {
  return (
    <output aria-live="polite" className="flex min-h-[40dvh] flex-1 items-center justify-center">
      <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent motion-reduce:animate-none" />
      <span className="sr-only">Loading</span>
    </output>
  );
}

/**
 * Purchase orders keep their success screen outside the form, so the dialog
 * owns the created result the way `PurchaseOrdersUnifiedPage` does.
 */
function PurchaseOrderDialogBody({
  headerAccessory,
  onLeave,
  outcomeAccessory,
}: {
  headerAccessory: ReactNode;
  onLeave: () => void;
  outcomeAccessory: ReactNode;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<CreatePurchaseOrderResult | null>(null);
  const [formKey, setFormKey] = useState(0);

  const refetchOrders = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
  }, [queryClient]);
  const refetchPayments = useCallback(
    () => queryClient.invalidateQueries({ queryKey: paymentKeys.vendor.all }),
    [queryClient],
  );

  if (result) {
    return (
      <DocumentCreateOutcomeFrame
        headerAccessory={outcomeAccessory}
        surface="dialog"
        title={DOCUMENT_CREATE_COPY.purchase_order.title}
      >
        <POSuccessScreen
          onCreateAnother={() => {
            setResult(null);
            setFormKey((key) => key + 1);
          }}
          onViewOrders={() => {
            void navigate("/purchase-orders");
          }}
          result={result}
          selectedVendor={null}
        />
      </DocumentCreateOutcomeFrame>
    );
  }

  return (
    <PurchaseOrderForm
      headerAccessory={headerAccessory}
      key={formKey}
      onLeave={onLeave}
      onRefetchOrders={refetchOrders}
      onRefetchPayments={refetchPayments}
      onSuccess={setResult}
      surface="dialog"
    />
  );
}

/**
 * Hosts a finance document's guided-steps wizard in a modal over the current
 * route, so a user can create from the dashboard or the quick-actions button
 * without losing their place. Full screen below `sm`; above it a centered
 * dialog of fixed height, so the footer buttons stay put from step to step.
 *
 * The wizard's own first-step Cancel closes the dialog through `onLeave` after
 * its draft confirm. The header close, Escape, and "Open full page" go through
 * this component's confirm instead, because the draft lives inside the
 * workflow and would otherwise be dropped silently. Outside clicks are
 * ignored for the same reason. Navigating anywhere (a success screen's
 * "View" link, the full-page action) closes the dialog via its provider.
 */
export function DocumentCreateDialog({ kind, onOpenChange, open }: DocumentCreateDialogProps) {
  const navigate = useNavigate();
  const [pendingLeave, setPendingLeave] = useState<PendingLeave | null>(null);
  const copy = DOCUMENT_CREATE_COPY[kind];
  const descriptionId = `document-create-dialog-${kind}-description`;

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const [previousOpen, setPreviousOpen] = useState(open);
  if (previousOpen !== open) {
    setPreviousOpen(open);
    if (!open) setPendingLeave(null);
  }

  const confirmLeave = () => {
    const action = pendingLeave;
    setPendingLeave(null);
    if (action === "expand") {
      void navigate(copy.route);
      return;
    }
    close();
  };

  const headerAccessory = (
    <>
      <SectionHeaderAction
        className="text-icon-secondary hover:text-icon-primary"
        icon={Expand}
        label="Open full page"
        onClick={() => setPendingLeave("expand")}
      />
      <SectionHeaderAction
        className="text-icon-secondary hover:text-icon-primary"
        icon={X}
        label="Close"
        onClick={() => setPendingLeave("close")}
      />
    </>
  );

  // Once the document exists nothing is pending, so the outcome screen closes
  // without a confirm. It offers no full-page action: that route starts a
  // fresh draft and would silently drop the created document's own actions.
  const outcomeAccessory = (
    <SectionHeaderAction
      className="text-icon-secondary hover:text-icon-primary"
      icon={X}
      label="Close"
      onClick={close}
    />
  );

  const workflowProps = {
    headerAccessory,
    onLeave: close,
    outcomeAccessory,
    surface: "dialog" as const,
  };

  return (
    <>
      <Dialog
        onOpenChange={(next) => (next ? onOpenChange(true) : setPendingLeave("close"))}
        open={open}
      >
        <DialogContent
          // The shared DialogContent drops Radix's default describedby, so the
          // description is wired by id.
          aria-describedby={descriptionId}
          className="surface-elevated-strong flex flex-col gap-0 overflow-hidden border-border/70 bg-background/95 p-0 max-sm:top-0 max-sm:left-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none sm:h-[min(92dvh,56rem)] sm:max-w-5xl"
          data-testid="document-create-dialog"
          hideCloseButton
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            setPendingLeave("close");
          }}
          onInteractOutside={(event) => event.preventDefault()}
        >
          {/* Distinct from the wizard's visible heading, which carries `copy.title`,
              so a screen reader does not announce the same string twice on open. */}
          <DialogTitle className="sr-only">Guided steps: {copy.title}</DialogTitle>
          <DialogDescription className="sr-only" id={descriptionId}>
            {copy.description}
          </DialogDescription>
          <Suspense fallback={<DialogPendingState />}>
            {kind === "invoice" ? <InvoiceCreateWorkflow {...workflowProps} /> : null}
            {kind === "estimate" ? <EstimateCreateWorkflow {...workflowProps} /> : null}
            {kind === "purchase_order" ? (
              <PurchaseOrderDialogBody
                headerAccessory={headerAccessory}
                onLeave={close}
                outcomeAccessory={outcomeAccessory}
              />
            ) : null}
          </Suspense>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        confirmLabel={LEAVE_COPY[pendingLeave ?? "close"].confirmLabel}
        description={LEAVE_COPY[pendingLeave ?? "close"].description}
        onConfirm={confirmLeave}
        onOpenChange={(next) => {
          if (!next) {
            setPendingLeave(null);
          }
        }}
        open={pendingLeave !== null}
        title={LEAVE_COPY[pendingLeave ?? "close"].title}
      />
    </>
  );
}
